import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import useWindowDimensions from "hooks/useWindowDimensions";
import HelpIcon from "assets/icons/HelpIcon";
import BookOpenIcon from "assets/icons/BookOpenIcon";
import FAQIcon from "assets/icons/FAQIcon";
import ParentsIcon from "assets/icons/ParentsIcon";
import DocumentIcon from "assets/icons/DocumentIcon";
import LockIcon from "assets/icons/LockIcon";
import LaptopIcon from "assets/icons/LaptopIcon";
import ArrowDownIcon from "assets/icons/ArrowDownIcon";
import {
  PARENTS_MATERIAL_PATH,
  USER_MANUAL_PATH,
  FAQ_PATH,
} from "router/pathName";

const HelpButton = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isMobileView = width < 1025;
  const iconColor = "#000000";
  const [isOpen, setIsOpen] = useState(false);
  const [listWidth, setListWidth] = useState(0);
  const [leftPos, setLeftPos] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const navigateWithPreviousPath = (path: string) => {
    navigate(path, {
      state: { from: location.pathname + location.search },
    });
    setIsOpen(false);
  };

  const options: { label: string; icon: ReactNode; onClick: () => void }[] = [
    {
      label: "_help._user_manual",
      icon: <BookOpenIcon width={22} height={22} color={iconColor} />,
      onClick: () => navigateWithPreviousPath(USER_MANUAL_PATH),
    },
    {
      label: "_help._faq",
      icon: <FAQIcon width={22} height={22} color={iconColor} />,
      onClick: () => navigateWithPreviousPath(FAQ_PATH),
    },
    {
      label: "_help._parents_material",
      icon: <ParentsIcon width={22} height={22} color={iconColor} />,
      onClick: () => navigateWithPreviousPath(PARENTS_MATERIAL_PATH),
    },
    {
      label: "_help._term_of_use",
      icon: <DocumentIcon width={22} height={22} color={iconColor} />,
      onClick: () => window.open("https://www.irodoki.com/term-of-use"),
    },
    {
      label: "_help._privacy_policy",
      icon: <LockIcon width={22} height={22} color={iconColor} />,
      onClick: () => window.open("https://www.irodoki.com/policy-privacy"),
    },
    {
      label: "_help._recommended_env",
      icon: <LaptopIcon width={22} height={22} color={iconColor} />,
      onClick: () =>
        window.open("https://www.irodoki.com/recommended-environment"),
    },
  ];

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const actualWidth = isMobileView
        ? width / 2
        : Math.max(width - rect.left, width / 6);
      setListWidth(actualWidth);
      setLeftPos(rect.left + actualWidth - width);
    }
  }, [isOpen, isMobileView, width]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        className="flex items-center gap-1 rounded-md px-1 py-1 text-black transition-colors hover:bg-[#F5F5F5]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <HelpIcon width={20} height={20} color={iconColor} />
        {!isMobileView ? (
          <>
            <span className="text-sm font-medium">{t("_help._support")}</span>
            <ArrowDownIcon width={16} height={16} color={iconColor} />
          </>
        ) : null}
      </button>
      {isOpen ? (
        <ul
          className="absolute z-40 list-none space-y-2 overflow-auto rounded-xl border border-[#D9D9D9] bg-white p-3 shadow-[0_12px_24px_rgba(0,0,0,0.12)]"
          style={{
            width: listWidth,
            left: -leftPos,
            top: isMobileView ? "calc(100% + 8px)" : "calc(100% + 10px)",
          }}
        >
          {options.map((option) => (
            <li key={option.label}>
              <button
                className="flex w-full items-center space-x-2 rounded-lg p-2 hover:bg-[#F4F4F4]"
                onClick={option.onClick}
              >
                {option.icon}
                <p className="text-left text-sm text-[#333333]">
                  {t(option.label)}
                </p>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default HelpButton;
